import * as THREE from 'three';
import { Palette, VoxelGrid } from '../core/voxel-grid.js';
import { createVoxelMaterial } from '../core/materials.js';
import { buildTerrain } from './terrain.js';
import { registerPropBlocks, buildVillage } from './props.js';
import { registerMillBlocks, createWindmill } from './windmill.js';
import { createLinkHouse } from './link-house.js';
import { registerBoatBlocks, createSailboat } from './sailboat.js';
import { registerMarketBlocks, createMarket } from './market.js';
import { registerVolendamBlocks, buildVolendam } from './volendam.js';
import { registerChurchBlocks } from './church.js';
import { registerFerryBlocks } from './ferry.js';
import { registerWharfBlocks, createWharf } from './wharf.js';
import { dressCountryside } from './countryside.js';
import { registerMarkenBlocks, buildMarken } from './marken.js';
import { registerStructureBlocks, populateRiver, createFairwayPost } from './structures.js';
import { createWater } from './water.js';
import { buildWorldDepth } from './depth-map.js';
import { createGround } from './ground.js';
import { weld } from '../core/merge.js';
import { PICK_MATERIAL } from '../core/materials.js';
import { createSky } from './sky.js';
import { createClouds } from './clouds.js';
import { WORLD, MILLS, HOUSES, SEA, FLEET, GOODS, ISLANDS, SITE } from '../config.js';

const nextFrame = () => new Promise((r) => requestAnimationFrame(() => r()));

/**
 * Build the whole polder.
 *
 * Generation is split into steps with a frame yielded between each, so the
 * loading bar reports work that is genuinely happening instead of running a
 * timer next to it.
 */
export async function buildWorld(env, onProgress = () => {}) {
    const { size, height, voxelSize, seaLevel } = WORLD;
    const seed = SITE.seed;

    const palette = new Palette();
    const scene = new THREE.Group();
    scene.name = 'polder';

    // The Zaanse Schans keeps the grid the rest of the world was written
    // against; the fishing villages get their own, smaller ones.
    const grid = new VoxelGrid(size, height, size);

    const step = async (fraction, label, fn) => {
        onProgress(fraction, label);
        await nextFrame();
        return fn();
    };

    // --- land -------------------------------------------------------------
    const polder = await step(0.05, 'Draining the polder', () =>
        buildTerrain(grid, palette, {
            size, seaLevel, seed, voxelSize,
            origin: { x: ISLANDS[0].x, z: ISLANDS[0].z },
        }),
    );

    // --- the other islands --------------------------------------------------
    // Each is its own grid and its own mesh, placed in the sea. The empty
    // water between them costs nothing this way, where one grid spanning all
    // three would cost ten times the terrain we have.
    const others = [];
    for (const spec of ISLANDS.filter((i) => i.built && i.id !== ISLANDS[0].id)) {
        others.push(await step(0.14, `Making landfall at ${spec.name}`, () => {
            const g = new VoxelGrid(spec.size, height, spec.size);
            const p = buildTerrain(g, palette, {
                size: spec.size,
                seaLevel,
                // Its own seed, or every island is the same island.
                seed: seed + spec.id.length * 7717,
                voxelSize,
                origin: { x: spec.x, z: spec.z },
                canal: spec.canal,
            });
            return { spec, grid: g, polder: p };
        }));
    }

    const propBlocks = await step(0.22, 'Digging the Zaan', () => registerPropBlocks(palette));
    const boatBlocks = registerBoatBlocks(palette);
    const marketBlocks = registerMarketBlocks(palette);
    const volendamBlocks = registerVolendamBlocks(palette);
    const markenBlocks = registerMarkenBlocks(palette);
    const churchBlocks = registerChurchBlocks(palette);
    const ferryBlocks = registerFerryBlocks(palette);
    const wharfBlocks = registerWharfBlocks(palette);
    const millBlocks = registerMillBlocks(palette);
    registerStructureBlocks(palette);

    // --- link sites, levelled before anything else is planted -------------
    const millSites = MILLS.map((cfg) => {
        const spot = polder.bankSpot(cfg.bank, cfg.offset);
        return { cfg, ...spot, level: seaLevel + 3 };
    });

    const houseSites = HOUSES.map((cfg) => {
        const spot = polder.bankSpot(cfg.bank, cfg.offset);
        return { cfg, ...spot, level: seaLevel + 3 };
    });

    await step(0.3, 'Levelling the mill yards', () => {
        for (const site of houseSites) {
            polder.flatten(site.x, site.z, 9, site.level);
        }
        for (const site of millSites) {
            polder.flatten(site.x, site.z, 12, site.level);
            // A short track from the towpath up to the mill door.
            const towpath = polder.bankSpot(site.cfg.bank, -9);
            const steps = 24;
            for (let i = 0; i <= steps; i++) {
                const x = Math.round(towpath.x + ((site.x - towpath.x) * i) / steps);
                const z = Math.round(towpath.z + ((site.z - towpath.z) * i) / steps);
                polder.paint(x, z, polder.blocks.path);
                polder.paint(x + 1, z, polder.blocks.path);
            }
        }
    });

    // --- village ----------------------------------------------------------
    const village = await step(0.42, 'Raising the village', () =>
        buildVillage(
            grid,
            propBlocks,
            polder,
            seed + 5,
            [
                ...millSites.map((s) => ({ x: s.x, z: s.z, r: 17 })),
                ...houseSites.map((s) => ({ x: s.x, z: s.z, r: 13 })),
            ],
        ),
    );

    // Everything voxel-built shares this, so it has to exist before anything
    // is built rather than when the ground mesh happens to be assembled.
    const terrainMaterial = createVoxelMaterial(env);

    // --- the villages on the other islands ---------------------------------
    // Built before the meshes go up, because they cut into the ground: the
    // harbour basin is carved out of Volendam rather than dropped on top of it.
    const villages = {};
    for (const other of others) {
        if (other.spec.id !== 'volendam') continue;
        villages.volendam = await step(0.46, 'Mooring the Volendam fleet', () =>
            buildVolendam(
                scene, other.grid, other.polder, palette, terrainMaterial,
                volendamBlocks, propBlocks, churchBlocks,
                {
                    voxelSize,
                    seed: seed + 4211,
                    // Open east, onto the water — towards Marken, which is
                    // where the real Volendam's harbour looks. The Zaanse
                    // Schans is inland of it; a harbour facing inland is a
                    // harbour on the wrong side of its own village.
                    facing: Math.atan2(
                        ISLANDS[2].x - other.spec.x,
                        ISLANDS[2].z - other.spec.z,
                    ),
                },
            ),
        );
        other.village = villages.volendam;
    }

    let marken = null;
    for (const other of others) {
        if (other.spec.id !== 'marken') continue;
        marken = await step(0.50, 'Lighting the Paard van Marken', () =>
            buildMarken(
                scene, other.grid, other.polder, palette, terrainMaterial,
                markenBlocks, propBlocks, env,
                {
                    voxelSize,
                    seed: seed + 8123,
                    // The quay looks at Volendam: that is the crossing.
                    facing: Math.atan2(
                        ISLANDS[1].x - other.spec.x,
                        ISLANDS[1].z - other.spec.z,
                    ),
                },
            ),
        );
    }

    // Everything behind the harbours: fields, sheep, poplars, reeds. Kept
    // clear of the basins and the houses that are already there.
    for (const other of others) {
        await step(0.52, `Grazing the ${other.spec.name} polder`, () => {
            const clear = [];
            if (other.village?.basin) {
                clear.push({
                    x: other.village.basin.cx,
                    z: other.village.basin.cz,
                    r: other.village.basin.radius + 6,
                });
            }
            dressCountryside(other.grid, propBlocks, other.polder, seed + other.spec.size, {
                keepClear: clear,
            });
        });
    }

    // --- register the practical lights ------------------------------------
    // Grid coordinates to world space, then handed to the environment so the
    // voxel shader can pool warm light on the ground after dark.
    const toWorld = (gx, gy, gz) => {
        const { x, z } = polder.toWorld(gx, gz);
        return new THREE.Vector3(x, gy * voxelSize, z);
    };

    await step(0.50, 'Lighting the lanterns', () => {
        for (const l of village.lights) {
            env.addLight(toWorld(l.x, l.y, l.z), {
                color: 0xffb861,
                intensity: 1,
                range: (l.range || 11) * voxelSize * 2.4,
            });
        }
        // A lamp at each linked house, so it reads as inhabited after dark.
        for (const site of houseSites) {
            env.addLight(toWorld(site.x, site.level + 3, site.z + 4), {
                color: 0xffc27a,
                intensity: 1.15,
                range: 12 * voxelSize * 2.4,
            });
        }
        // A lamp over each mill door — the busiest light on the river.
        for (const site of millSites) {
            env.addLight(toWorld(site.x, site.level + 3, site.z + 8), {
                color: 0xffc27a,
                intensity: 1.35,
                range: 15 * voxelSize * 2.6,
            });
        }
    });

    // --- one big mesh for all of it ---------------------------------------
    const terrainMesh = await step(0.62, 'Stacking 400,000 voxels', () => {
        const geometry = grid.build(palette, {
            voxelSize,
            // Where this island's grid sits in the world.
            origin: new THREE.Vector3(
                polder.origin.x - polder.span / 2,
                0,
                polder.origin.z - polder.span / 2,
            ),
            // Nothing below this is ever visible through the water.
            cullBelow: seaLevel - 5,
        });
        const mesh = new THREE.Mesh(geometry, terrainMaterial);
        mesh.name = 'terrain';
        for (const other of others) {
            const g = other.grid.build(palette, {
                voxelSize,
                origin: new THREE.Vector3(
                    other.polder.origin.x - other.polder.span / 2,
                    0,
                    other.polder.origin.z - other.polder.span / 2,
                ),
                cullBelow: seaLevel - 5,
            });
            const m = new THREE.Mesh(g, terrainMaterial);
            m.name = `terrain:${other.spec.id}`;
            other.mesh = m;
            scene.add(m);
        }
        scene.add(mesh);
        return mesh;
    });

    // --- the two mills ----------------------------------------------------
    const mills = await step(0.76, 'Hoisting the sails', () => {
        const built = [];
        for (const site of millSites) {
            const mill = createWindmill(
                { env, palette, voxelSize, millBlocks },
                site.cfg,
            );
            mill.group.position.copy(toWorld(site.x, site.level, site.z));
            scene.add(mill.group);
            built.push(mill);
        }
        return built;
    });

    // --- the linked houses ------------------------------------------------
    const linkHouses = await step(0.80, 'Lighting the lockkeeper in', () => {
        const built = [];
        for (const site of houseSites) {
            const house = createLinkHouse(
                { env, palette, voxelSize, propBlocks },
                site.cfg,
            );
            house.group.position.copy(toWorld(site.x, site.level, site.z));
            scene.add(house.group);
            built.push(house);
        }
        return built;
    });

    const polderOf = (id) => others.find((o) => o.spec.id === id)?.polder || polder;

    // --- the fish market ---------------------------------------------------
    // In Volendam's harbour, where a fish market belongs: the catch is landed
    // at the quay the botters are moored along, not on a beach.
    const market = await step(0.81, 'Opening the fish market', () => {
        const vol = others.find((o) => o.spec.id === 'volendam');
        if (!vol?.village) return null;

        const { basin, mouth } = vol.village;
        const p = vol.polder;
        const middle = p.toWorld(basin.cx, basin.cz);

        // The stall stands on the landward rim, with its jetty reaching out
        // into the basin — so a boat ties up inside the harbour.
        const inward = mouth + Math.PI;
        const rim = basin.radius * voxelSize;
        const foot = {
            x: middle.x + Math.sin(inward) * rim,
            z: middle.z + Math.cos(inward) * rim,
        };

        const built = createMarket(palette, terrainMaterial, marketBlocks, {
            voxelSize,
            // Half the basin's radius: enough to tie up against, not enough to
            // reach across the harbour and block it.
            length: Math.max(8, Math.round(basin.radius * 0.5)),
        });
        // The deck, not the base: two and a half voxels above the waterline, so
        // the swell passes under it rather than over it — it was sitting below
        // the surface — and on a half-voxel so it never lands exactly on a
        // terrain face, which is what was z-fighting against the beach.
        built.group.position.set(foot.x, (seaLevel + 2.5) * voxelSize, foot.z);
        // Local +X runs out along the jetty: back towards the water.
        built.group.rotation.y = Math.atan2(-Math.sin(inward), -Math.cos(inward)) - Math.PI / 2;
        scene.add(built.group);

        const head = {
            x: foot.x - Math.sin(inward) * built.length,
            z: foot.z - Math.cos(inward) * built.length,
        };
        built.pier = {
            ax: foot.x, az: foot.z,
            bx: head.x, bz: head.z,
            r: built.halfWidth + 1.1,
        };
        // Alongside the jetty rather than off its end: a boat lies against the
        // side of a pier, and the end is exactly where the pier's own
        // collision radius is, so a berth there is inside the timber.
        const beam = built.halfWidth + 2.2;
        built.mooring = new THREE.Vector3(
            (foot.x + head.x) / 2 + Math.cos(inward) * beam,
            seaLevel * voxelSize,
            (foot.z + head.z) / 2 - Math.sin(inward) * beam,
        );

        env.addLight(built.mooring.clone().setY((seaLevel + 4) * voxelSize), {
            color: 0xffc27a, intensity: 1.3, range: 16 * voxelSize * 2.6,
        });
        return built;
    });

    // --- the mill wharves --------------------------------------------------
    // On the seaward side of the Zaanse Schans, out on the coast behind each
    // mill: the goods came down to the water rather than the water going up to
    // the mills.
    const wharves = await step(0.83, 'Loading the mill wharves', () => {
        const built = [];
        for (const good of GOODS) {
            const site = millSites.find((m) => m.cfg.id === good.mill);
            if (!site) continue;

            // Straight out from the island's middle through the mill, until
            // the ground gives way to open water.
            const from = polder.toWorld(site.x, site.z);
            const bearing = Math.atan2(from.x - polder.origin.x, from.z - polder.origin.z);
            let coast = 0;
            for (let r = 0; r < polder.span / 2; r += 0.4) {
                const x = polder.origin.x + Math.sin(bearing) * r;
                const z = polder.origin.z + Math.cos(bearing) * r;
                if (polder.covers(x, z) && polder.topAtWorld(x, z) >= seaLevel) coast = r;
            }
            if (!coast) continue;

            const w = createWharf(palette, terrainMaterial, wharfBlocks, {
                voxelSize, goods: good.id,
            });
            const foot = {
                x: polder.origin.x + Math.sin(bearing) * (coast - 1.5),
                z: polder.origin.z + Math.cos(bearing) * (coast - 1.5),
            };
            w.mesh.position.set(foot.x, (seaLevel + 2.5) * voxelSize, foot.z);
            w.mesh.rotation.y = bearing - Math.PI / 2;
            scene.add(w.mesh);

            const head = {
                x: foot.x + Math.sin(bearing) * w.length,
                z: foot.z + Math.cos(bearing) * w.length,
            };
            env.addLight(
                new THREE.Vector3(head.x, (seaLevel + 3) * voxelSize, head.z),
                { color: 0xffc27a, intensity: 1, range: 11 * voxelSize * 2.4 },
            );

            // Something to point at, so a wharf can say what it is selling.
            const pick = new THREE.Mesh(
                new THREE.CylinderGeometry(6, 7, 8, 10),
                PICK_MATERIAL,
            );
            pick.position.set(foot.x, (seaLevel + 4) * voxelSize, foot.z);
            pick.userData.wharf = good.id;
            scene.add(pick);

            built.push({
                ...good,
                pick,
                bearing,
                landing: new THREE.Vector3(foot.x, (seaLevel + 2) * voxelSize, foot.z),
                pier: {
                    ax: foot.x, az: foot.z, bx: head.x, bz: head.z,
                    r: w.halfWidth + 1.1,
                },
            });
        }
        return built;
    });

    // --- the visitor's boat -----------------------------------------------
    const sailboat = await step(0.83, 'Bending on the mainsail', () => {
        const boat = createSailboat(palette, terrainMaterial, boatBlocks, { voxelSize });
        scene.add(boat.group);
        return boat;
    });

    // --- river furniture --------------------------------------------------
    const river = await step(0.86, 'Mooring the barges', () =>
        populateRiver(scene, polder, palette, terrainMaterial, { voxelSize, seed: seed + 11 }),
    );

    // --- the shoal ring ---------------------------------------------------
    // Marks standing in open water, well beyond the last voxel of terrain.
    // The water shader draws the shallows they stand on; these make the edge
    // of the sea room something you can see from a distance and steer by.
    await step(0.90, 'Marking the shoals', () => {
        const posts = [];
        for (let i = 0; i < SEA.shoalPosts; i++) {
            const a = (i / SEA.shoalPosts) * Math.PI * 2;
            const scale = SEA.shoalRadius * 0.04;
            const wobble = (Math.sin(a * 3) * 1.6 + Math.sin(a * 7 + 1.1) * 0.9) * scale;
            const r = SEA.shoalRadius + wobble;
            const post = createFairwayPost(palette, terrainMaterial, { voxelSize, signal: false });
            post.group.position.set(
                Math.cos(a) * r,
                (seaLevel - 2) * voxelSize,
                Math.sin(a) * r,
            );
            post.setLit(false);
            post.group.updateMatrixWorld(true);
            posts.push(post.group);
        }

        // Two dozen posts that never move and never light: one mesh, one draw.
        const ring = weld(posts, terrainMaterial, 'shoal-ring');
        if (ring) scene.add(ring);
    });

    // --- water, sky, weather ---------------------------------------------
    const water = await step(0.92, 'Letting the water in', () => {
        // Every island's ground in one coarse texture, so the shallows and the
        // foam line happen wherever there is land — not only where the map
        // used to be built from.
        const depth = buildWorldDepth([{ polder }, ...others], {
            extent: Math.max(SEA.shoalRadius * 2.6, 420),
        });
        const mesh = createWater(env, { seaLevel, depth }, {
            voxelSize, shoalRadius: SEA.shoalRadius,
        });
        scene.add(mesh);
        return mesh;
    });

    const sky = createSky(env);
    const clouds = await step(0.97, 'Rolling in the weather', () => {
        const group = createClouds(env, palette, { voxelSize, seed: seed + 3 });
        scene.add(group);
        return group;
    });

    onProgress(1, 'Welkom');

    // Which palette entry a relay flag colour corresponds to.
    const flagIdFor = (flagHex) => boatBlocks.flags[Math.max(0, FLEET.flags.indexOf(flagHex))];

    // Build another visitor's boat, flying the flag the relay gave them.
    const makeBoat = (flagHex) => createSailboat(palette, terrainMaterial, boatBlocks, {
        voxelSize,
        flagId: flagIdFor(flagHex),
    });

    // The links are the houses now. The mills are landmarks: they have
    // something to say and nowhere to send you.
    const links = [...linkHouses];

    // World-space position of each link, for camera framing and HUD labels.
    for (const link of links) {
        link.worldAnchor = link.group.position.clone().add(link.labelAnchor);
    }

    // Everything that has something to say: the mills, the lighthouse, the
    // church. Each pick carries its own card and its own place to hang it, so
    // nothing outside has to know which is which.
    const landmarks = [];
    for (const mill of mills) {
        const anchor = mill.pick.userData.anchor.clone().add(mill.group.position);
        mill.pick.userData.anchor = anchor;
        landmarks.push(mill.pick);
    }
    if (marken) {
        marken.pick.userData.anchor = marken.position.clone().setY(marken.position.y + 4);
        landmarks.push(marken.pick);
    }
    if (villages.volendam?.spire) {
        const spire = villages.volendam.spire;
        spire.pick.userData.anchor = spire.position.clone();
        landmarks.push(spire.pick);
    }

    return {
        scene,
        sky,
        polder,
        ground: createGround([polder, ...others.map((o) => o.polder)], seaLevel),
        islands: [{ spec: ISLANDS[0], polder }, ...others],
        mills,
        links,
        landmarks,
        sailboat,
        market,
        marken,
        church: villages.volendam?.spire || null,
        // Where the crossing calls: the market jetty at Volendam, the stone
        // landing at Marken.
        ports: [
            market && {
                id: 'volendam',
                landing: market.group.position.clone().setY((seaLevel + 2) * voxelSize),
                mooring: market.mooring,
                facing: (villages.volendam?.mouth ?? 0),
                // The jetty, as a line: you lie against its side, anywhere
                // along it. A circle round one end is either too tight at the
                // other or too loose everywhere.
                line: market.pier,
            },
            marken?.quay && {
                id: 'marken',
                landing: marken.quay.landing,
                mooring: marken.quay.mooring,
                facing: marken.quay.facing,
                line: marken.quay.line,
            },
        ].filter(Boolean).map((port) => {
            // Something to point at, so a quay can say what it is paying and
            // how many are waiting on it.
            const pick = new THREE.Mesh(
                new THREE.CylinderGeometry(7, 8, 9, 10),
                PICK_MATERIAL,
            );
            pick.position.copy(port.landing).setY(port.landing.y + 3);
            pick.userData.port = port.id;
            scene.add(pick);
            return { ...port, pick };
        }),
        wharves,
        ferryBlocks,
        palette,
        material: terrainMaterial,
        makeBoat,
        flagIdFor,
        water,
        clouds,
        river,
        terrainMesh,
        palette,
        voxelSize,
        // Every island, not just the first — the stats line is meant to say
        // what the machine is actually drawing.
        triangleCount: [terrainMesh, ...others.map((o) => o.mesh)]
            .reduce((n, m) => n + (m?.geometry.attributes.position.count || 0) / 3, 0),
    };
}
